'use client'

import { useState, useEffect, useContext } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import BillingPayment from './BillingPayment'
import BillingSuccess from './BillingSuccess'
import BillingTierSelect from './BillingTierSelect'
import { CheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
const steps = ['Select Tier', 'Payment', 'Success']

export default function BillingTierModal({
  open,
  onClose,
  currentTier,
  onTierUpdate,
  billingDetails,
}) {
  const stripePromise = process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY
    ? loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY)
    : null
  const [activeStep, setActiveStep] = useState(0)
  const [selectedTier, setSelectedTier] = useState(null)
  const [teamSize, setTeamSize] = useState(1)
  const [tiers, setTiers] = useState([])
  const authContext = useContext(AuthContext)
  const [paymentLoading, setPaymentLoading] = useState(true)
  const [sessionId, setSessionId] = useState(null)
  const [couponCode, setCouponCode] = useState('')
  const [checkoutKey, setCheckoutKey] = useState(0)
  const [clientSecret, setClientSecret] = useState(null)
  const [couponDiscount, setCouponDiscount] = useState(null)

  useEffect(() => {
    if (open) fetchTiers()
  }, [open])

  const fetchTiers = async () => {
    try {
      const { data } = await axios(
        `/organization/${authContext.user.organization.id}/tiers`,
        { method: 'GET' },
        authContext,
        true,
        'billing',
      )
      setTiers(data)
    } catch (err) {
      console.error('Failed to fetch tiers:', err)
    }
  }

  const resetModal = () => {
    setActiveStep(0)
    setSelectedTier(null)
    setPaymentLoading(true)
    setCouponCode('')
    setClientSecret(null)
    setSessionId(null)
    setCouponDiscount(null)
    setCheckoutKey(prev => prev + 1)
  }

  const handleClose = () => {
    onClose()
    setTimeout(resetModal, 300)
  }

  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1)
  }

  const handleTierSelect = tier => {
    setSelectedTier(tier)
    setTeamSize(tier.minTeamSize)
    handleNext()
  }

  const createSession = async (couponCodeForSession = '') => {
    setPaymentLoading(true)
    try {
      const { data } = await axios(
        `/organization/${authContext.user.organization.id}/checkout-session`,
        {
          method: 'POST',
          data: {
            tier: selectedTier.name,
            teamSize,
            couponCode: couponCodeForSession?.trim() || undefined,
          },
        },
        authContext,
        true,
        'billing',
      )
      setSessionId(data.sessionId)
      setClientSecret(data.clientSecret)

      if (data.appliedCoupon) {
        setCouponDiscount(data.appliedCoupon)
      }

      return data.clientSecret
    } finally {
      setPaymentLoading(false)
    }
  }

  const fetchClientSecret = () => {
    if (clientSecret) {
      return Promise.resolve(clientSecret)
    }
    return createSession('')
  }

  const handleCouponApply = async (newCouponCode, discountInfo) => {
    setCouponDiscount(discountInfo)
    await createSession(newCouponCode)
    setCheckoutKey(prev => prev + 1)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) handleClose()
      }}
    >
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select Billing Tier</DialogTitle>
        </DialogHeader>
        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {steps.map((label, idx) => (
            <div key={label} className="flex items-center gap-2">
              <span
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium',
                  idx <= activeStep
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {idx + 1}. {label}
              </span>
              {idx < steps.length - 1 && (
                <div
                  className={cn(
                    'h-px w-8',
                    idx < activeStep ? 'bg-primary' : 'bg-muted',
                  )}
                />
              )}
            </div>
          ))}
        </div>
        <div className="billing-tier-modal">
          {activeStep === 0 && (
            <BillingTierSelect
              tiers={tiers}
              currentTier={billingDetails?.canceledAtEnd ? null : currentTier}
              handleTierSelect={handleTierSelect}
              handleClose={handleClose}
            />
          )}
          {activeStep === 1 && (
            <>
              {paymentLoading && (
                <div
                  className="flex items-center justify-center"
                  style={{ height: 400 }}
                >
                  <Loader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
              )}
              <div style={{ display: paymentLoading ? 'none' : 'block' }}>
                <CheckoutProvider
                  key={checkoutKey}
                  stripe={stripePromise}
                  options={{
                    fetchClientSecret: fetchClientSecret,
                  }}
                >
                  <BillingPayment
                    selectedTier={selectedTier}
                    teamSize={teamSize}
                    setTeamSize={setTeamSize}
                    onTierUpdate={onTierUpdate}
                    handleNext={handleNext}
                    handleClose={handleClose}
                    handleBack={handleBack}
                    setLoading={setPaymentLoading}
                    sessionId={sessionId}
                    tiers={tiers}
                    couponCode={couponCode}
                    setCouponCode={setCouponCode}
                    onCouponApply={handleCouponApply}
                    initialCouponDiscount={couponDiscount}
                  />
                </CheckoutProvider>
              </div>
            </>
          )}
          {activeStep === 2 && (
            <BillingSuccess
              selectedTier={selectedTier}
              handleClose={handleClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
